import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UpdateFormPageAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { FabButtonNextScreenAction } from '@app/finxone-web-frontend/app/lib/actions/navigation.action';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { phoneNumberValidator } from '@app/finxone-web-frontend/app/lib/utils/onboarding/utils';
import { removeAsterisk, setCssAttributes } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetConfig,
  WidgetNames,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { parsePhoneNumber } from 'libphonenumber-js';
import { FormActionTypeEnum } from '../../../../../../../../../libs/shared/enums/form-action.enum';
import { Subject, Subscription, delay, map, takeUntil } from 'rxjs';
import { Country } from '../../../../../lib/dtos/countries.dto';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';
import { MetadataService } from '../../../../../lib/services/metadata-service/metadata-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../../lib/services/multipart-holder/multipart-form-holder.service';
import { StyleServiceService } from '../../../../../lib/services/style-service/style-service.service';
import { PageConfigurationProvider } from '../../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-phone-number',
  templateUrl: './phone-number.component.html',
  styleUrls: ['./phone-number.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PhoneNumberComponent extends FormSubmitter implements OnInit, OnDestroy {
  @Output() formValuesChange = new EventEmitter<any>();
  @Input() formActionStateData: { [key: string]: string };
  @Input() dynamicZone = false;
  @Input() fieldAttributesPhone: any[];
  public widgetConfig: UiZoneWidgetConfig;
  public zone = 'onboarding';
  public userProfileData: any;
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  public dialCodeOptions: string[] = [];
  public phoneNumber = '';
  public isError = false;
  public isStepComplete = false;
  public isShowErrorMsg = false;
  public phoneForm: FormGroup;
  private phoneVerified = false;
  public showConfirmBtn = false;
  public showWaitingIcon = false;
  public showCheckedIcon = false;
  public errorMsg = '';
  public isFieldDisabled = false;
  public colorTextAlignStyle: baseCssConfigStyle;
  public colorStyle: baseCssConfigStyle;
  public borderStyle: baseCssConfigStyle;
  private verificationPopupResponseSubscriptionPhone: Subscription;
  private nextPageUrl: string;
  public phoneFormValue: { [key: string]: string | number } = {};
  public isPhoneVerificationEnabled: boolean;
  public isShowSuccessIcon = false;
  public isShowErrorIcon = false;
  public textInputFontStyle: baseCssConfigStyle;
  public textInputBackgroundColor: baseCssConfigStyle;
  public numberInputConfig: any;
  public combinedContentAndBorderStyles: baseCssConfigStyle;
  public inputStyle: baseCssConfigStyle;
  public inputBaseStyle: baseCssConfigStyle;
  public inputInactiveStyle: baseCssConfigStyle;
  public currentInputFieldStyle: baseCssConfigStyle;
  public currentInputDropdownStyle: baseCssConfigStyle;
  private isValueChanged = true;
  private formActionData: Record<string, any> | null;
  public dropdownWidth = '75px';
  private formActionStateId: FormActionTypeEnum;
  private lastVerifiedPhone: string;
  public phoneString = '';
  public verifyButtonStyle: baseCssConfigStyle;
  public verifyButtonTextContent: string;
  public countryCodeDropdownStyle: baseCssConfigStyle;
  public isWorkflow: boolean;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public countryDropdowntextInputFontStyle: baseCssConfigStyle;

  private configService = inject(ConfigService);
  private metaService = inject(MetadataService);
  private formHolderService = inject(MultiPartFormHolderService);
  private urlZoneInput = inject(ZonePageUrlProvider);
  public dataIn = inject<ProviderClass<any>>(ProviderClass);
  private formSubmissionService = inject(FormSubmissionService);
  public pageConfig = inject(PageConfigurationProvider);
  private styleService = inject(StyleServiceService);
  private _router = inject(Router);
  private alertHandlerService = inject(AlertHandlerService);

  constructor() {
    super(inject(Store));
    this.zone = this.urlZoneInput.zoneUrlInput;
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }
  }

  ngOnInit(): void {
    this.onCheckPhoneVerificationFlag();
    this.userProfileData = this.store.selectSnapshot(ProfileState.getProfile);
    let isVerified = false;
    let countryCode;
    let phoneNumber;
    if (this.zone !== formatNameAsUrl(APP_ZONES.ONBOARDING) && !this.dynamicZone) {
      isVerified = this.formActionData?.['isPhoneVerified'] ?? this.userProfileData?.phoneVerified;
      this.lastVerifiedPhone = this.userProfileData.phoneNumber;
      phoneNumber =
        this.formActionData?.['phone'] ?? this.userProfileData.phoneNumber
          ? parsePhoneNumber(this.userProfileData.phoneNumber).nationalNumber
          : '';
      countryCode =
        this.formActionData?.['country_code'] ?? this.userProfileData.phoneNumber
          ? '+' + parsePhoneNumber(this.userProfileData.phoneNumber).countryCallingCode
          : '';
    }
    const formGroupExtension = this.getPhoneVerificationField(isVerified);
    const form = new FormGroup({
      phone: new FormControl(phoneNumber ?? '', [
        Validators.required,
        Validators.maxLength(15),
        phoneNumberValidator('country_code'),
      ]),
      // Default to +44 will get updated if not onboarding page
      country_code: new FormControl(countryCode ?? '+44', Validators.required),
      ...formGroupExtension,
    });
    this.determineDropdownWidth(form?.get('country_code')?.value ?? '');
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.PHONE_WIDGET,
      route: '/onboard-individual/register',
      form: form,
    });

    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);

    this.isFieldDisabled = false;
    if (this.dataIn) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
      this.numberInputConfig = this.widgetConfig.attributes['field_attributes'].filter(
        (attri: any) => attri.type !== 'dropdown',
      );
      if (this.dynamicZone && this.fieldAttributesPhone) {
        this.numberInputConfig = this.fieldAttributesPhone;
      }
    }

    this.getFormStateIdFromZone();
    this.buildForm();
    this.subscribeDataFormState(this.formActionStateId);
    this.getCountriesList();
    this.onCheckPhoneVerificationFlag();
    this.checkPhoneSuccessErrorIconFlag();
    this.setCssAttributes();
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['verifyButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['verifyButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['verifyButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['verifyButton'].widgetProperties?.['globalStyling'] ?? false;
    }
  }

  private subscribeDataFormState(id?: string) {
    if (!this.dynamicZone) {
      this.subscribeToFormAction(id)
        .pipe(
          takeUntil(this.destroy$),
          map((response) => {
            if (response?.formData?.['phone']) {
              this.formActionData = response.formData;
              if (response?.formData?.['isPhoneVerified']) {
                this.lastVerifiedPhone = response.formData['country_code'] + response.formData['phone'];
              }
              if (this.phoneForm?.get('phone')) this.patchFormValues(response.formData);
            }
            if (response?.type === 'submitStep') {
              this.checkPhoneVerificationValidation();
            }

            if (response?.nextPageUrl) this.nextPageUrl = response.nextPageUrl;
            if (response?.type === 'submitStep') {
              if (this.phoneForm.valid) {
                this.onSubmit();
              } else {
                this.phoneForm.markAllAsTouched();
              }
            }
          }),
        )
        .subscribe();
      //if no existing form data add default value (profile data)
      if (!this.formActionData?.['phone'] && !this.formActionData?.['isPhoneVerified']) {
        this.formSubmissionService.storeFormData(this.phoneForm.value, id);
      }
    } else {
      this.formActionData = this.formActionStateData;
      this.patchFormValues(this.formActionStateData);
    }
  }

  checkPhoneVerificationValidation() {
    if (this.isPhoneVerificationEnabled && !this.phoneVerified && this.phoneForm.get('phone')?.valid) {
      this.showConfirmBtn = true;
      this.isStepComplete = false;
      this.isError = true;
      this.errorMsg = 'Phone number needs validation before proceeding to the next step.';
      this.showErrorAlert(this.errorMsg);
    }
  }

  private showErrorAlert(message: string) {
    this.alertHandlerService.showAlertFn('error', message);
  }

  private takeFormDataValueFromExistingFormAction() {
    let existingFormPhoneNumber = this.userProfileData.phoneNumber;
    if (this.formActionData?.['phone'] && this.formActionData?.['country_code']) {
      existingFormPhoneNumber = this.formActionData?.['country_code'] + this.formActionData?.['phone'];
    }
    return existingFormPhoneNumber;
  }

  public countryCodeInputChange(event: string) {
    this.phoneFormValue['country_code'] = event;
    this.phoneForm?.get('country_code')?.patchValue(event);
    this.determineDropdownWidth(event);
    this.isValueChanged = true;
    this.performZoneSpecificActions(this.phoneForm.value);
  }

  private setCssAttributes() {
    const {
      contentStyle,
      colorTextAlignFontStyle,
      colorStyle,
      borderStyle,
      textInputFontStyle,
      textInputBackgroundColor,
    } = setCssAttributes(this.styleService, this.configService, this.attri);

    this.combinedContentAndBorderStyles = {
      ...contentStyle,
      ...borderStyle,
    };
    this.contentStyle = contentStyle;
    this.colorTextAlignStyle = {
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
        BaseCssProperties.FONT_SIZE,
        BaseCssProperties.TEXT_FONT_STYLE,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.COLOR,
      ]),
    };
    this.colorStyle = colorStyle;
    this.borderStyle = borderStyle;
    this.textInputFontStyle = textInputFontStyle;
    this.textInputBackgroundColor = textInputBackgroundColor;
    const { baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.attri.widgetProperties,
    );

    this.inputStyle = baseStyle;
    this.inputBaseStyle = activeStyle;
    this.inputInactiveStyle = inactiveStyle;
    this.currentInputFieldStyle = this.currentInputDropdownStyle = {
      ...this.inputBaseStyle,
      ...this.inputInactiveStyle,
    };
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    if (this.dataIn.widgetConfig?.nestedWidgets?.['verifyButton']) {
      this.verifyButtonStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'verifyButton'),
        buttonNestedWidgetConfigStyles,
      );
      this.verifyButtonTextContent = this.getNestedWidgetConfig(
        this.dataIn,
        'verifyButton',
      )?.widgetProperties?.textContent;
    } else {
      this.verifyButtonStyle = {};
    }
    if (this.dataIn.widgetConfig?.nestedWidgets?.['countryCodeDropdown']) {
      this.countryCodeDropdownStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'countryCodeDropdown'),
        buttonNestedWidgetConfigStyles,
      );
      this.countryDropdowntextInputFontStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'countryCodeDropdown'),
        [
          BaseCssProperties.FONT_SIZE,
          BaseCssProperties.FONT_WEIGHT,
          BaseCssProperties.TEXT_BOLD,
          BaseCssProperties.TEXT_FONT_STYLE,
          BaseCssProperties.TEXT_ITALIC,
          BaseCssProperties.TEXT_UNDERLINE,
          BaseCssProperties.V_ALIGN,
          BaseCssProperties.TEXTALIGN,
          BaseCssProperties.COLOR,
        ],
      );
    } else {
      this.countryCodeDropdownStyle = {};
    }
  }

  private checkPhoneSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }
  private onCheckPhoneVerificationFlag() {
    if (this.dataIn && this.widgetConfig) {
      const elementsConfig = this.widgetConfig.attributes['elementsConfig'];

      const verificationOtpField = elementsConfig?.find((item) => item.field_name === 'verificationViaOTP');

      if (verificationOtpField) {
        this.isPhoneVerificationEnabled = verificationOtpField.show;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.verificationPopupResponseSubscriptionPhone) {
      this.verificationPopupResponseSubscriptionPhone.unsubscribe();
    }
  }
  public updateCurrentStyles(event: boolean, field: string) {
    if (field === 'input') {
      this.currentInputFieldStyle = {
        ...this.inputBaseStyle,
        ...(event ? this.inputStyle : this.inputInactiveStyle),
      };
    } else {
      this.currentInputDropdownStyle = {
        ...this.inputBaseStyle,
        ...(event ? this.inputStyle : this.inputInactiveStyle),
      };
    }
  }

  private getCountriesList(): void {
    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((countries: Country[]) => {
          if (countries.length) {
            const sortByCode = (code: string) => (code === 'GB' || code === 'IN' ? -1 : 1);
            this.dialCodeOptions = countries
              .sort(
                (a, b) =>
                  sortByCode(a.code) - sortByCode(b.code) || parseInt(a.dial_code) - parseInt(b.dial_code),
              )
              .map((item) => item.dial_code);
            // remove duplicates
            this.dialCodeOptions = [...new Set(this.dialCodeOptions)];
            return this.userProfileData?.country_code ?? this.dialCodeOptions?.[0];
          }
        }),
        delay(100),
      )
      .subscribe();
  }

  public showErrorMsg() {
    this.isShowErrorMsg = !this.isShowErrorMsg;
  }

  private buildForm() {
    const stage = this.formHolderService.config.getStage(WidgetNames.PHONE_WIDGET);
    this.phoneForm = stage.getForm();
    this.updateFormAction();
  }
  private performZoneSpecificActions(formValues: Record<string, string>) {
    switch (this.zone) {
      case formatNameAsUrl(APP_ZONES.ONBOARDING):
        this.performOnboardingZoneActions();
        break;
      case formatNameAsUrl(APP_ZONES.VERIFICATION):
        this.performKycZoneActions(formValues);
        break;
      case formatNameAsUrl(APP_ZONES.PROFILE):
        this.performProfileZoneActions(formValues);
        break;
      default:
        console.error(`No actions defined for ${this.zone}, please check`);
        break;
    }
  }
  private performOnboardingZoneActions() {
    if (this.phoneForm.valid && this.isValueChanged) {
      this.formSubmissionService.storeFormData(this.phoneForm.value);
      this.isValueChanged = false;
    }
    if (this.phoneForm.get('isPhoneVerified')?.invalid && this.phoneForm.get('phone')?.touched) {
      this.showConfirmBtn = true;
      this.isError = true;
      this.isStepComplete = false;
    }
  }
  private performKycZoneActions(formValues: Record<string, string>) {
    const phonePreviousValue = this.takeFormDataValueFromExistingFormAction();
    if (this.phoneForm.valid && this.isValueChanged) {
      if (formValues['country_code'] + formValues['phone'] !== phonePreviousValue) {
        this.phoneForm.get('isPhoneVerified')?.patchValue(false);
      }
      this.formSubmissionService.storeFormData(this.phoneForm.value, FormActionTypeEnum.KYC_DETAILS);
      this.isValueChanged = false;
    }
  }
  private performProfileZoneActions(formValues: Record<string, string>) {
    const phonePreviousValue = this.takeFormDataValueFromExistingFormAction();

    if (this.phoneForm.valid && this.isValueChanged) {
      if (formValues['country_code'] + formValues['phone'] !== phonePreviousValue) {
        this.phoneForm.get('isPhoneVerified')?.patchValue(false);
      }
      console.log('phone is looping');
      this.formSubmissionService.storeFormData(this.phoneForm.value, FormActionTypeEnum.EDIT_PROFILE_DETAILS);
      this.isValueChanged = false;
    }
    if (this.phoneForm.get('isPhoneVerified')?.invalid && this.phoneForm.get('phone')?.touched) {
      this.showConfirmBtn = true;
    }
  }

  private patchFormValues(formData: Record<string, string>) {
    const formIndex = this.formSubmissionService.currentPageFormGroups?.findIndex((item) =>
      item.get('phone'),
    );
    this.formSubmissionService.updateCurrentPageFormGroups(formIndex, this.phoneForm);
    this.phoneForm.get('phone')?.patchValue(formData?.['phone']);
    this.phoneForm.get('country_code')?.patchValue(formData?.['country_code'] ?? '+44');
    this.phoneFormValue['phone'] = formData?.['phone'];
    this.phoneFormValue['country_code'] = formData?.['country_code'];
    this.phoneString = this.phoneFormValue['country_code'] + ' - ' + this.phoneFormValue['phone'];
    this.isStepComplete = true;
    this.showCheckedIcon = true;
    this.showWaitingIcon = false;
    this.isError = false;
    this.phoneVerified = formData?.['isPhoneVerified'] as unknown as boolean;
    this.phoneForm.get('isPhoneVerified')?.setValue(formData?.['isPhoneVerified'] ?? false);
    if (this.isPhoneVerificationEnabled && !this.phoneVerified) {
      this.showConfirmBtn = true;
      this.isStepComplete = false;
    }
  }

  public onSubmit() {
    if (this.phoneForm.valid && (this.phoneVerified || !this.showConfirmBtn)) {
      const formValues = this.phoneForm.value;
      this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then((success) => {
        if (success) {
          this.isStepComplete = true;
          this.isError = false;

          this.store.dispatch(
            new UpdateFormPageAction({
              formPages: {
                'phone-number': { isVerified: true },
              },
            }),
          );

          // Form submission was successful, you can handle it here
        } else {
          // Handle submission failure
        }
      });
    } else {
      this.phoneForm.markAllAsTouched();
      this.isStepComplete = false;
      this.isError = true;
      this.errorMsg = 'Please enter valid phone number and confirm using OTP.';
    }
  }

  public onChangePhoneInput(): void {
    if (this.phoneForm.valid && (this.phoneVerified || !this.showConfirmBtn)) {
      this.store.dispatch(new FabButtonNextScreenAction());
    } else {
      this.phoneForm.markAllAsTouched();
      this.showConfirmBtn = true;
      this.isError = true;
      this.isStepComplete = false;
      this.errorMsg = 'Please enter valid phone number and confirm using OTP.';
    }
  }

  public verifyPhoneNumber() {
    if (this.phoneForm.get('country_code')?.valid && this.phoneForm.get('phone')?.valid) {
      this.showConfirmBtn = false;
      this.showWaitingIcon = true;
      const urlExtension = `/${this.urlZoneInput.role}/${this.urlZoneInput.subscription}`;
      const redirectUrl =
        this.zone === formatNameAsUrl(APP_ZONES.ONBOARDING)
          ? `/zones/${this.zone}/phone-confirmation${urlExtension}`
          : `/zones/${this.zone}/phone-confirmation`;
      this._router.navigateByUrl(redirectUrl);
    } else {
      this.alertHandlerService.showAlertFn('error', 'Entered mobile phone number must be valid');
    }
  }

  public valueChangesTrigger() {
    this.isValueChanged = true;
    if (
      this.phoneForm.get('country_code')?.value + this.phoneForm.get('phone')?.value ===
      this.lastVerifiedPhone
    ) {
      this.phoneForm.get('isPhoneVerified')?.patchValue(true);
      this.formSubmissionService.storeFormData(this.phoneForm.value, this.formActionStateId);
      this.showConfirmBtn = false;
      return;
    }
    if (this.isPhoneVerificationEnabled) {
      this.phoneForm.get('isPhoneVerified')?.patchValue(false);
      const formValues = this.phoneForm.value;
      this.isStepComplete = false;
      // show error
      this.isError = true;
      this.phoneForm.setErrors({ invalid: true });
      if (this.formActionStateId) {
        this.formSubmissionService.storeFormData(this.phoneForm.value, this.formActionStateId);
      } else {
        this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then((success) => {
          if (success) {
            this.store.dispatch(
              new UpdateFormPageAction({
                formPages: {
                  'phone-number': { isVerified: false },
                },
              }),
            );
          }
        });
      }
    } else {
      this.phoneForm.patchValue({ isPhoneVerified: true });
      this.isStepComplete = true;
      this.isError = false;
    }
    this.updateFormAction();
  }
  public verifiedPhoneChanged() {
    if (this.isPhoneVerificationEnabled) {
      this.showConfirmBtn = true;
    }
    this.showCheckedIcon = false;
    this.showWaitingIcon = false;
    this.phoneVerified = false;
    this.metaService.verificationFlag.next({
      fieldName: WidgetNames.PHONE_WIDGET,
      isVerified: false,
    });
  }

  private updateFormAction() {
    if (!this.dynamicZone && this.isValueChanged) this.performZoneSpecificActions(this.phoneForm.value);
    if (this.phoneForm.valid || this.isValueChanged) {
      this.formValuesChange.emit(this.phoneForm.value);
    }
    this.isValueChanged = false;
  }

  public checkCountryCodeValueChange() {
    let countryCodeValue = '';
    if (this.lastVerifiedPhone) {
      countryCodeValue = '+' + parsePhoneNumber(this.lastVerifiedPhone).countryCallingCode;
    }
    this.isValueChanged = true;
    this.determineDropdownWidth(this.phoneForm.get('country_code')?.value);
    if (this.phoneForm.get('country_code')?.value !== countryCodeValue) {
      this.phoneForm.get('isPhoneVerified')?.patchValue(false);
      this.showConfirmBtn = true;
    } else {
      this.phoneForm.get('isPhoneVerified')?.patchValue(true);
      this.showConfirmBtn = false;
    }
    this.valueChangesTrigger();
  }
  private getPhoneVerificationField(isVerified: boolean) {
    return {
      isPhoneVerified: new FormControl(this.isPhoneVerificationEnabled ? isVerified : true, [
        Validators.requiredTrue,
      ]),
    };
  }
  public phoneInputChange() {
    this.isValueChanged = true;
  }

  // This method determines the width of a dropdown based on the length of a given string, `countryCodeValue`.
  private determineDropdownWidth(countryCodeValue: string) {
    // If the `countryCodeValue` is empty or null, set a default width of '60px' for the dropdown.
    if (!countryCodeValue) {
      this.dropdownWidth = '60px';
      return;
    }
    // Average width in pixels for each character in the dropdown, used to estimate total width.
    const averageCharWidth = 20;
    const padding = 20;
    // Multiplies the length of `countryCodeValue` by the average character width and adds padding.
    this.dropdownWidth = `${countryCodeValue.length * averageCharWidth + padding}px`;
  }

  private getFormStateIdFromZone() {
    switch (this.zone) {
      case formatNameAsUrl(APP_ZONES.VERIFICATION):
        this.formActionStateId = FormActionTypeEnum.KYC_DETAILS;
        break;
      case formatNameAsUrl(APP_ZONES.PROFILE):
        this.formActionStateId = FormActionTypeEnum.EDIT_PROFILE_DETAILS;
        break;
      default:
        return;
    }
  }

  public sanitizeFieldLabel(label: string) {
    return removeAsterisk(label);
  }
}
